import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './TeacherFreeTier.css';
import NotebookPanel from './NotebookPanel';

/**
 * TeacherLiteDashboard — landing dashboard for free-forever teacher tier.
 *
 * Per docs/FREE_TEACHER_TIER_SCOPE.md Day 3.
 *
 * Does NOT require school_license_id (unlike the full MorpheusInterface).
 * Shows: class info, active roster (or placeholder), mocked outcome preview,
 * referral CTA, upgrade-to-school CTA.
 */

const TeacherLiteDashboard = ({ user, onLogout }) => {
  const [roster, setRoster] = useState([]);
  const [classCode, setClassCode] = useState(null);
  const [className, setClassName] = useState('My class');
  const [classes, setClasses] = useState([]);        // multi-class (one per period)
  const [maxClasses, setMaxClasses] = useState(6);
  const [addingClass, setAddingClass] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Day 4 endpoint — graceful fallback to empty state if not yet shipped
    const fetchClass = async () => {
      try {
        const response = await axios.get('/api/classroom/my-class');
        const data = response.data || {};
        if (data.students) setRoster(data.students);
        if (data.code) setClassCode(data.code);
        if (data.class_name) setClassName(data.class_name);
        if (data.classes) setClasses(data.classes);
        if (data.max_classes) setMaxClasses(data.max_classes);
      } catch (err) {
        // No class created yet — show empty state
      } finally {
        setLoading(false);
      }
    };
    fetchClass();
  }, []);

  const goToOnboarding = () => navigate('/teacher/onboarding');
  const goToShare = () => navigate('/teacher/share');
  const goToUpgrade = () => navigate('/upgrade?source=teacher_dashboard&target=school');

  const addClass = async () => {
    const name = window.prompt('Name the class (e.g. Period 3 \u2014 AP Gov):');
    if (!name || !name.trim()) return;
    setAddingClass(true);
    try {
      const r = await axios.post('/api/classroom/generate-class-code',
        { class_name: name.trim(), new_class: true });
      if (r.data.success) {
        const refreshed = await axios.get('/api/classroom/my-class');
        if (refreshed.data.classes) setClasses(refreshed.data.classes);
      } else {
        window.alert(r.data.error || 'Could not create class');
      }
    } catch (err) {
      window.alert(err.response?.data?.error || 'Could not create class');
    } finally { setAddingClass(false); }
  };

  return (
    <div className="teacher-lite-page">
      {/* Top bar */}
      <div className="teacher-lite-topbar">
        <div className="teacher-lite-brand">
          <img src="/images/TAPPY-logo-black.png" alt="Tappy" className="teacher-lite-logo" />
          <span className="teacher-lite-tier-badge">Free</span>
        </div>
        <div className="teacher-lite-topbar-actions">
          <span className="teacher-lite-user">{user?.name || user?.username}</span>
          <button className="teacher-btn-ghost" onClick={onLogout}>Sign out</button>
        </div>
      </div>

      <div className="teacher-lite-main">
        {/* Welcome / primary panel */}
        <section className="teacher-lite-section">
          <h1 className="teacher-lite-heading">{classes.length > 1 ? 'Your classes' : className}</h1>
          <p className="teacher-lite-subhead">
            Free-forever tier · up to {maxClasses} classes of 30 students · unlimited Mira sessions
          </p>

          {classCode ? (
            <>
              {(classes.length ? classes : [{ invite_id: 0, code: classCode, class_name: className, students: roster, student_count: roster.length, max_students: 30 }]).map((c) => (
                <div key={c.invite_id} className="teacher-code-display teacher-code-inline" style={{ marginBottom: 10 }}>
                  <div>
                    <div className="teacher-code-label">{c.class_name}</div>
                    <div className="teacher-code-value">{c.code}</div>
                  </div>
                  <span className="teacher-lite-student-stats" style={{ marginRight: 'auto', marginLeft: 12 }}>
                    {c.student_count}/{c.max_students || 30}
                  </span>
                  {/* Scannable join QR — teacher projects the dashboard,
                      students scan. Generated client-side. */}
                  <QRCodeSVG
                    value={`${window.location.origin.replace('app.', '')}/class-join?code=${c.code}`}
                    size={72}
                    fgColor="#1b3f48"
                    style={{ marginRight: 12, flexShrink: 0 }}
                  />
                  <button
                    className="teacher-btn-secondary"
                    onClick={() => navigator.clipboard?.writeText(c.code)}
                  >
                    Copy
                  </button>
                </div>
              ))}
              {classes.length < maxClasses && (
                <button className="teacher-btn-secondary" onClick={addClass} disabled={addingClass}>
                  {addingClass ? 'Creating…' : '+ Add a class (another period)'}
                </button>
              )}
            </>
          ) : (
            <div className="teacher-lite-cta-card">
              <p>You haven't created a class code yet. Generate one to start inviting students.</p>
              <button className="teacher-btn-primary" onClick={goToOnboarding}>
                Set up my class →
              </button>
            </div>
          )}
        </section>

        {/* Class materials & assignments (NotebookLM outcome loop) */}
        <NotebookPanel />

        {/* Roster */}
        <section className="teacher-lite-section">
          <h2 className="teacher-lite-subheading">Students</h2>
          {loading ? (
            <div className="teacher-lite-empty">Loading...</div>
          ) : roster.length === 0 ? (
            <div className="teacher-lite-empty">
              <p>Your first student will appear here once they join with your class code.</p>
              <p className="teacher-lite-empty-hint">
                Students go to teachtappy.ai/class-join and enter the code + their first name.
                No email or password needed.
              </p>
            </div>
          ) : classes.length > 1 ? (
            classes.map((c) => (
              <div key={c.invite_id} style={{ marginBottom: 12 }}>
                <div className="teacher-lite-subhead" style={{ marginBottom: 6 }}>
                  {c.class_name} · {c.student_count} student{c.student_count === 1 ? '' : 's'}
                </div>
                <div className="teacher-lite-roster">
                  {c.students.map((student) => (
                    <div key={student.id} className="teacher-lite-student-row">
                      <div className="teacher-lite-student-name">{student.first_name}</div>
                      <div className="teacher-lite-student-stats">
                        {student.sessions} sessions · mastery {Math.round((student.mastery || 0) * 100)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="teacher-lite-roster">
              {roster.map((student) => (
                <div key={student.id} className="teacher-lite-student-row">
                  <div className="teacher-lite-student-name">{student.first_name}</div>
                  <div className="teacher-lite-student-stats">
                    {student.sessions} sessions · mastery {Math.round((student.mastery || 0) * 100)}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Outcome preview — faded mock until real data exists */}
        <section className="teacher-lite-section teacher-lite-preview">
          <h2 className="teacher-lite-subheading">What you'll see after their first session</h2>
          <div className="teacher-lite-preview-card">
            <div className="teacher-lite-preview-row">
              <span>Mastery by topic</span>
              <span className="teacher-lite-preview-muted">Fractions · Photosynthesis · Pythagorean theorem</span>
            </div>
            <div className="teacher-lite-preview-row">
              <span>Misconceptions to address</span>
              <span className="teacher-lite-preview-muted">Teacher dashboard surfaces patterns across students</span>
            </div>
            <div className="teacher-lite-preview-row">
              <span>Minutes saved</span>
              <span className="teacher-lite-preview-muted">Estimated re-teach time spared by Mira</span>
            </div>
          </div>
          <p className="teacher-lite-preview-hint">
            This is a preview. Real data appears here as soon as a student completes a session.
          </p>
        </section>

        {/* Referral + upgrade */}
        <section className="teacher-lite-section teacher-lite-cta-grid">
          <div className="teacher-lite-cta-card">
            <h3 className="teacher-lite-cta-title">Share with a colleague</h3>
            <p>Teachers who refer get early access to new Tappy features. Free tier never expires.</p>
            <button className="teacher-btn-secondary" onClick={goToShare}>
              Invite a colleague
            </button>
          </div>

          <div className="teacher-lite-cta-card teacher-lite-cta-upgrade">
            <h3 className="teacher-lite-cta-title">Ready for your whole school?</h3>
            <p>
              Unlock Canvas / Schoology / Classroom integration, gradebook passback,
              roster sync, and unlimited students. Our team will work with your district on a DPA.
            </p>
            <button className="teacher-btn-primary" onClick={goToUpgrade}>
              See school plans
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TeacherLiteDashboard;
